import { Dialog } from '@headlessui/react';
import clsx from 'clsx';
import { isValidElement } from 'react';

import {
  ModalBackdrop,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalStyles,
} from './index';
import type { ModalBaseProps, ModalComponent } from './modal-types';

const ModalBase = (props: ModalBaseProps) => {
  const {
    className = '',
    bgClass = 'bg-white',
    roundedClass,
    size = 'md',
    header = '',
    headerClasses = '',
    body = '',
    bodyClasses = '',
    footer = '',
    footerClasses = '',
    children = '',
    backdrop = true,
    centered = true,
    onClose,
    isOpen,
    ...restProps
  } = props;

  // Modal window sizes & classes
  const sizeClasses = ModalStyles.modalSizes[size];
  // Filter out bg-white (replaced by bgClass); replace 'rounded' with roundedClass when provided.
  const baseClasses = ModalStyles.modalBase
    .filter((cls) => cls !== 'bg-white')
    .map((cls) => (cls === 'rounded' && roundedClass ? roundedClass : cls));
  const modalClasses = clsx(baseClasses, bgClass, sizeClasses, className);

  const returnModalPanel = () => {
    return (
      <Dialog.Panel className={modalClasses}>
        {isValidElement(header)
          ? header
          : header && (
              <ModalHeader
                content={header}
                className={headerClasses}
                onClose={onClose}
              />
            )}
        {isValidElement(body)
          ? body
          : body && (
              <ModalBody
                content={body}
                className={bodyClasses}
                onClose={onClose}
              />
            )}
        {children}
        {isValidElement(footer)
          ? footer
          : footer && (
              <ModalFooter
                content={footer}
                className={footerClasses}
                onClose={onClose}
              />
            )}
      </Dialog.Panel>
    );
  };

  return (
    <Dialog
      open={isOpen}
      className='relative z-40'
      onClose={onClose}
      {...restProps}>
      {backdrop && <ModalBackdrop />}
      <div className='fixed w-screen inset-0 overflow-y-auto'>
        <div
          className={clsx(
            'flex',
            'min-h-full',
            'justify-center',
            centered ? 'items-center' : 'items-start pt-10',
            size !== 'full' && 'p-2',
          )}>
          {returnModalPanel()}
        </div>
      </div>
    </Dialog>
  );
};

const StyledDialog: ModalComponent = Object.assign(ModalBase, {
  Backdrop: ModalBackdrop,
  Header: ModalHeader,
  Body: ModalBody,
  Footer: ModalFooter,
});

export { StyledDialog as Modal };
